import type { Branch, Member } from '@/api/types'
import { t } from '@/i18n'
import { checkImageFile, type LogoCandidate, type LogoCheck } from './logo-file'

/// Clubs (branches) of a company — what the panel shows and offers.
/// SPEC: specs/tenancy.md §1. An archived club is never deleted: other
/// services keep its id, so it stays in the list the server sends and the
/// panel decides where it appears.

export const isArchived = (branch: Pick<Branch, 'status'>): boolean => branch.status === 'archived'

/// Open clubs first, in the order they were opened; the archive after.
export const splitBranches = (
  branches: readonly Branch[],
): Readonly<{ active: readonly Branch[]; archived: readonly Branch[] }> => ({
  active: branches.filter((b) => !isArchived(b)),
  archived: branches.filter(isArchived),
})

/// What a member's club picker offers: every open club, plus an archived one
/// only while the person is still limited to it — so it can be unticked, and
/// never silently kept where nobody can see it.
export const pickableBranches = (
  branches: readonly Branch[],
  selected: readonly string[],
): readonly Branch[] => branches.filter((b) => !isArchived(b) || selected.includes(b.id))

/// «Київ, вул. Героїв Дніпра, 1» — or nothing.
export const branchPlace = (branch: Pick<Branch, 'city' | 'address'>): string =>
  [branch.city, branch.address].filter((part): part is string => Boolean(part?.trim())).join(', ')

export type ScopeLine =
  | Readonly<{ kind: 'all' }>
  | Readonly<{ kind: 'none' }>
  | Readonly<{ kind: 'some'; names: readonly string[]; archived: readonly string[] }>

/// Which clubs a staff row names. Empty scope = the whole company. Ids that
/// no longer resolve are dropped; if none resolve, the row says so instead
/// of pretending it is «all».
export const scopeLine = (scope: readonly string[], branches: readonly Branch[]): ScopeLine => {
  if (scope.length === 0) return { kind: 'all' }
  const named = scope
    .map((id) => branches.find((b) => b.id === id))
    .filter((b): b is Branch => Boolean(b))
  if (named.length === 0) return { kind: 'none' }
  return {
    kind: 'some',
    names: named.filter((b) => !isArchived(b)).map((b) => b.name),
    archived: named.filter(isArchived).map((b) => b.name),
  }
}

/// Who an archive touches. `only`: every club the person is limited to is
/// this one (or already archived) — after it, they have no open club left.
/// `also`: limited to this one among others that stay open.
export const affectedByArchive = (
  branchId: string,
  members: readonly Member[],
  branches: readonly Branch[],
): Readonly<{ only: readonly Member[]; also: readonly Member[] }> => {
  const open = (id: string) => id !== branchId && branches.some((b) => b.id === id && !isArchived(b))
  const limited = members.filter((m) => m.branchScope.includes(branchId))
  return {
    only: limited.filter((m) => !m.branchScope.some(open)),
    also: limited.filter((m) => m.branchScope.some(open)),
  }
}

export type BranchDraft = Readonly<{ name: string; city: string; address: string; timezone: string }>

export const draftFromBranch = (branch: Branch | null, defaultZone: string): BranchDraft => ({
  name: branch?.name ?? '',
  city: branch?.city ?? '',
  address: branch?.address ?? '',
  timezone: branch?.timezone ?? defaultZone,
})

export type BranchInput = Readonly<{
  name: string
  city?: string | null
  address?: string | null
  timezone: string
}>

/// The create body: blank optional fields are left out.
export const draftToCreate = (draft: BranchDraft): BranchInput => ({
  name: draft.name.trim(),
  ...(draft.city.trim() ? { city: draft.city.trim() } : {}),
  ...(draft.address.trim() ? { address: draft.address.trim() } : {}),
  timezone: draft.timezone,
})

/// The PUT body: only what changed; a field emptied becomes null (cleared).
export const draftToUpdate = (draft: BranchDraft, branch: Branch): Partial<BranchInput> => {
  const city = draft.city.trim() || null
  const address = draft.address.trim() || null
  return {
    ...(draft.name.trim() !== branch.name ? { name: draft.name.trim() } : {}),
    ...(city !== branch.city ? { city } : {}),
    ...(address !== branch.address ? { address } : {}),
    ...(draft.timezone !== branch.timezone ? { timezone: draft.timezone } : {}),
  }
}

// ---- the club's place on «Зали поруч» (specs/partner-clubs.md §1, §4) ----

export type BranchLocation = Readonly<{ lat: number; lon: number; gymId: string | null }>

/// The stored place, or null. `lat` and `lon` travel together (PC1): one
/// without the other is no place.
export const locationOf = (branch: Pick<Branch, 'lat' | 'lon' | 'gymId'>): BranchLocation | null =>
  typeof branch.lat === 'number' && typeof branch.lon === 'number'
    ? { lat: branch.lat, lon: branch.lon, gymId: branch.gymId ?? null }
    : null

/// «48,450367» or «48.450367» → 48.450367; anything else → null. Six
/// decimals is ~10 cm: what a map pin means.
export const parseCoordinate = (text: string): number | null => {
  const clean = text.trim().replace(',', '.').replace(/[°\s]/g, '')
  if (!/^[-+−]?\d{1,3}(\.\d+)?$/.test(clean)) return null
  const value = Number(clean.replace('−', '-'))
  return Number.isFinite(value) ? value : null
}

const pairInRange = (lat: number | null, lon: number | null) =>
  lat !== null && lon !== null && Math.abs(lat) <= 90 && Math.abs(lon) <= 180 ? { lat, lon } : null

/// «48.450367, 22.705463» pasted from a map → both numbers; null if the text
/// is not such a pair. Only a comma-and-space or plain space separates them,
/// so a decimal comma inside one number is not mistaken for the separator.
export const parseCoordinatePair = (text: string): Readonly<{ lat: number; lon: number }> | null => {
  const parts = text.trim().split(/\s*;\s*|,\s+|\s+/).filter(Boolean)
  if (parts.length === 2) return pairInRange(parseCoordinate(parts[0] ?? ''), parseCoordinate(parts[1] ?? ''))
  // «48.450367,22.705463» — a bare comma between two dotted numbers.
  const bare = /^([-+−]?\d{1,3}\.\d+),([-+−]?\d{1,3}\.\d+)$/.exec(text.trim())
  return bare?.[1] && bare[2] ? pairInRange(parseCoordinate(bare[1]), parseCoordinate(bare[2])) : null
}

export type LocationDraft = Readonly<{ lat: string; lon: string; gymId: string | null }>

export const coordinateText = (value: number | null | undefined): string =>
  typeof value === 'number' ? String(Number(value.toFixed(6))) : ''

export const locationDraftOf = (branch: Pick<Branch, 'lat' | 'lon' | 'gymId'> | null): LocationDraft => ({
  lat: coordinateText(branch?.lat),
  lon: coordinateText(branch?.lon),
  gymId: branch?.gymId ?? null,
})

export type LocationProblems = Readonly<{ lat?: string; lon?: string }>

/// Both empty is «not on the map»; both filled must be numbers in range; one
/// of the two is no place at all.
export const locationProblems = (draft: LocationDraft): LocationProblems => {
  const l = t.sale.location
  const latText = draft.lat.trim()
  const lonText = draft.lon.trim()
  if (!latText && !lonText) return {}
  if (!latText || !lonText) return latText ? { lon: l.pairInvalid } : { lat: l.pairInvalid }
  const lat = parseCoordinate(latText)
  const lon = parseCoordinate(lonText)
  return {
    ...(lat === null || Math.abs(lat) > 90 ? { lat: l.latInvalid } : {}),
    ...(lon === null || Math.abs(lon) > 180 ? { lon: l.lonInvalid } : {}),
  }
}

export type LocationUpdate = Readonly<{ lat?: number | null; lon?: number | null; gymId?: string | null }>

/// The PUT fields for the place: only what changed, `lat` and `lon` always
/// together. Cleared coordinates also unlink the Overture place — a club off
/// the map claims nothing on it.
export const locationToUpdate = (draft: LocationDraft, branch: Pick<Branch, 'lat' | 'lon' | 'gymId'>): LocationUpdate => {
  const lat = parseCoordinate(draft.lat)
  const lon = parseCoordinate(draft.lon)
  const before = locationOf(branch)
  if (lat === null || lon === null) {
    return before || branch.gymId ? { lat: null, lon: null, ...(branch.gymId ? { gymId: null } : {}) } : {}
  }
  const moved = !before || before.lat !== lat || before.lon !== lon
  const gymId = draft.gymId
  return {
    ...(moved ? { lat, lon } : {}),
    ...(gymId !== (branch.gymId ?? null) ? { gymId } : {}),
  }
}

/// Great-circle metres — the same haversine services/gyms ranks by.
export const distanceMetres = (
  a: Readonly<{ lat: number; lon: number }>,
  b: Readonly<{ lat: number; lon: number }>,
): number => {
  const rad = (deg: number) => (deg * Math.PI) / 180
  const dLat = rad(b.lat - a.lat)
  const dLon = rad(b.lon - a.lon)
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2
  return 2 * 6_371_000 * Math.asin(Math.sqrt(h))
}

type Candidate = Readonly<{
  id: string
  name: string
  lat: number
  lon: number
  address: string | null
  city: string | null
  partner?: boolean
}>

const sameCity = (a: string | null | undefined, b: string | null | undefined): boolean =>
  Boolean(a?.trim() && b?.trim() && a.trim().toLocaleLowerCase('uk') === b.trim().toLocaleLowerCase('uk'))

/// The place the SAVED branch is linked to, as a row of the picker. The gyms
/// service hides a claimed Overture place under the club's own pin, so a
/// linked club never finds its place in the search — this row stands in for
/// it, from the branch's own point (copied from that place when linked).
export const currentGymPlace = (
  branch: Pick<Branch, 'name' | 'address' | 'city' | 'lat' | 'lon' | 'gymId'>,
): (Candidate & { current: true }) | null => {
  const point = locationOf({ lat: branch.lat ?? null, lon: branch.lon ?? null, gymId: null })
  return branch.gymId && point
    ? { id: branch.gymId, name: branch.name, ...point, address: branch.address, city: branch.city, current: true }
    : null
}

/// The search's answer as candidates for «це наш зал»: never a partner (a
/// club, not a place); the saved link first (currentGymPlace — hidden from
/// the search), then the place picked in this edit, the club's own city,
/// the nearest to its current point, the server's order otherwise.
export const rankGymCandidates = <G extends Candidate>(
  gyms: readonly G[],
  branch: Pick<Branch, 'name' | 'address' | 'city' | 'lat' | 'lon' | 'gymId'>,
  chosen: string | null = branch.gymId ?? null,
): readonly (G | (Candidate & { current: true }))[] => {
  const here = locationOf({ lat: branch.lat ?? null, lon: branch.lon ?? null, gymId: null })
  const current = currentGymPlace(branch)
  const ranked = gyms
    .filter((gym) => !gym.partner && gym.id !== current?.id)
    .map((gym, index) => ({
      gym,
      index,
      linked: gym.id === chosen ? 0 : 1,
      city: sameCity(gym.city, branch.city) ? 0 : 1,
      distance: here ? distanceMetres(here, gym) : 0,
    }))
    .sort((a, b) => a.linked - b.linked || a.city - b.city || a.distance - b.distance || a.index - b.index)
    .map((entry) => entry.gym)
  return current ? [current, ...ranked] : ranked
}

/// The point on OpenStreetMap — a plain link, nothing loaded from it here.
export const mapLink = (lat: number, lon: number): string =>
  `https://www.openstreetmap.org/?mlat=${coordinateText(lat)}&mlon=${coordinateText(lon)}#map=18/${coordinateText(lat)}/${coordinateText(lon)}`

/// «48.450367, 22.705463» — how a place is shown and copied.
export const coordinatesLine = (location: Readonly<{ lat: number; lon: number }>): string =>
  `${coordinateText(location.lat)}, ${coordinateText(location.lon)}`

// ---- the club's cover (specs/partner-clubs.md §1a) ----

/// Mirrors MAX_COVER_BYTES in services/orgs — a phone shot of the hall.
export const MAX_COVER_BYTES = 5 * 1024 * 1024

export const checkCoverFile = (file: LogoCandidate): LogoCheck => checkImageFile(file, MAX_COVER_BYTES)
